import { Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { Bar, BarChart, CartesianGrid, LabelList, ReferenceLine, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, CURSOR_FILL, GRID_STROKE, TOOLTIP_CONTENT_STYLE } from '@/components/charts/recharts-theme';
import { fmtDate, money, short } from './flow-helpers';
import type { CurrencyCode, FlowTx } from './flow-types';
import { INCOMING_COLOR, NET_COLOR, OUTGOING_COLOR } from './flows-constants';

const NET_CHART_HEIGHT = 260;
const STRIP_HEIGHT = 32;
const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };
const STRIP_MARGIN = { top: 4, right: 8, bottom: 4, left: 0 };
const SYNC_ID = 'treasury-flow-trend';
const Y_AXIS_WIDTH = 64;
const MAX_BAR_SIZE = 24;
const PILL_HEIGHT = 18;
const PILL_PADDING = 7;
const PILL_CHAR_WIDTH = 6.5;
// Dark ink on the pastel fills in both themes
const PILL_TEXT_COLOR = '#1a1a1a';
const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

interface FlowRow extends FlowTx {
  day: string;
}

interface StripCellProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string;
}

interface StripCellFactoryProps {
  color: string;
  unit: CurrencyCode;
}

/** One cell per day, centred on that day's band: an amount pill where money moved, a faint dot elsewhere so the row reads as a timeline */
const AmountStripCell = ({ x, y, width, height, value, color, unit }: StripCellProps & StripCellFactoryProps) => {
  console.log(`AmountStripCell `, { x, y, width, height, value, color, unit });
  const amount = Number(value);
  const cx = Number(x) + Number(width) / 2;
  const cy = Number(y) + Number(height) / 2;

  if (!amount) return <circle cx={cx} cy={cy} fill="hsl(var(--muted-foreground))" opacity={0.35} r={1.5} />;

  const text = short(amount, unit, 0);
  const pillWidth = PILL_PADDING * 2 + text.length * PILL_CHAR_WIDTH;

  return (
    <g>
      <rect fill={color} height={PILL_HEIGHT} rx={PILL_HEIGHT / 2} width={pillWidth} x={cx - pillWidth / 2} y={cy - PILL_HEIGHT / 2} />
      <text dominantBaseline="central" fill={PILL_TEXT_COLOR} fontSize={10} fontWeight={600} textAnchor="middle" x={cx} y={cy}>
        {text}
      </text>
    </g>
  );
};
AmountStripCell.displayName = 'AmountStripCell';

const TrendLegend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="size-2.5 rounded-sm" style={{ backgroundColor: NET_COLOR }} />
      Net flow
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="h-2.5 w-4 rounded-full" style={{ backgroundColor: INCOMING_COLOR }} />
      Incoming
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="h-2.5 w-4 rounded-full" style={{ backgroundColor: OUTGOING_COLOR }} />
      Outgoing
    </span>
  </div>
);
TrendLegend.displayName = 'TrendLegend';

const FlowTooltip = ({ active, payload, unit }: TooltipProps<number, string> & { unit: CurrencyCode }) => {
  const row = payload?.[0]?.payload as FlowRow | undefined;

  if (!active || !row) return null;

  return (
    <div className="flex flex-col gap-1 px-3 py-2 text-xs" style={TOOLTIP_CONTENT_STYLE}>
      <span className="font-medium">{fmtDate(row.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-sm" style={{ backgroundColor: NET_COLOR }} />
        Net: {money(row.net, unit)}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: INCOMING_COLOR }} />
        Incoming: {money(row.incoming, unit)}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: OUTGOING_COLOR }} />
        Outgoing: {money(row.outgoing, unit)}
      </span>
    </div>
  );
};
FlowTooltip.displayName = 'FlowTooltip';

interface StripProps {
  data: FlowRow[];
  dataKey: 'incoming' | 'outgoing';
  name: string;
  color: string;
  unit: CurrencyCode;
}

/** Strips share the net chart's days and hover rather than a second y-axis: amounts and net sit on very different scales */
const Strip = ({ data, dataKey, name, color, unit }: StripProps) => (
  <ResponsiveContainer height={STRIP_HEIGHT} width="100%">
    <BarChart accessibilityLayer data={data.map((row) => ({ ...row, lane: 1 }))} margin={STRIP_MARGIN} syncId={SYNC_ID}>
      <XAxis dataKey="day" hide />
      {/* Visible but blank: a hidden axis reserves no width, which would shift the strip's days off the bars above */}
      <YAxis axisLine={false} domain={[0, 1]} tick={false} tickLine={false} width={Y_AXIS_WIDTH} />
      <Tooltip content={<FlowTooltip unit={unit} />} cursor={CURSOR_FILL} />
      <Bar dataKey="lane" fill="transparent" isAnimationActive={false} name={name}>
        <LabelList content={<AmountStripCell color={color} unit={unit} />} dataKey={dataKey} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);
Strip.displayName = 'Strip';

export interface TrendChartProps {
  data: FlowTx[];
  unit: CurrencyCode;
  label: string;
}

export function AssetFlowTrendChart({ data, unit, label }: TrendChartProps) {
  const rows: FlowRow[] = data.map((d) => ({ ...d, day: fmtDate(d.date) }));
  console.log(rows);

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Net flow, last {data.length} days</CardTitle>
        <TrendLegend />
      </CardHeader>
      <CardContent aria-label={`Bar chart of net flow with incoming and outgoing amounts for ${label}`} className="flex flex-col gap-1" role="img">
        <ResponsiveContainer height={NET_CHART_HEIGHT} width="100%">
          <BarChart data={rows} margin={CHART_MARGIN} syncId={SYNC_ID}>
            <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" minTickGap={24} tick={AXIS_TICK} />
            <YAxis tick={AXIS_TICK} tickFormatter={(v: number) => short(v, unit)} width={Y_AXIS_WIDTH} />
            <Tooltip content={<FlowTooltip unit={unit} />} cursor={CURSOR_FILL} />
            <ReferenceLine stroke="hsl(var(--muted-foreground))" strokeDasharray="4 3" y={0} />
            <Bar dataKey="net" fill={NET_COLOR} maxBarSize={MAX_BAR_SIZE} name="Net flow" radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
        <Strip color={INCOMING_COLOR} data={rows} dataKey="incoming" name="Incoming" unit={unit} />
        <Strip color={OUTGOING_COLOR} data={rows} dataKey="outgoing" name="Outgoing" unit={unit} />
      </CardContent>
    </Card>
  );
}
